import * as React from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { ServerDataTable, type TableRow, type TableState } from '@/components/visitone/server-data-table'
import { RefusedSurface } from '@/components/visitone/surfaces/access-surface'
import { MembersPanel } from '@/components/visitone/surfaces/people/members-panel'
import { PeopleFrame } from '@/components/visitone/surfaces/people/people-shared'
import { RoleEditorPanel, type RoleEditorTarget } from '@/components/visitone/surfaces/people/role-editor-panel'
import { useT } from '@/components/visitone/visitone-provider'
import { useAccess } from '@/hooks/use-access'
import { usePeopleMutations, useRoles } from '@/hooks/use-people'
import { PeopleRequestError, type Holder, type RoleRow } from '@/lib/bff/people'
import { GRANTABLE_ACTIONS, type GrantableAction } from '@/lib/people/grantable-actions'
import { builtInDescription, errorCode, errorStatus, focusWithin, roleLabel } from '@/lib/people/people-text'

type Result =
  | { kind: 'created'; role: string }
  | { kind: 'saved'; role: string }
  | { kind: 'membersSaved'; role: string }
  | { kind: 'deleted'; role: string }

/*
 * Setup › People & access › Roles (VIS-332): the built-in roles and the
 * tenant's custom roles, with who holds each. Members needs `roles.assign`;
 * Create, Edit and Delete need `roles.define`. The BFF decides per role
 * whether the caller can assign it (`canAssign`) and change it (`canChange`).
 */
export function PeopleRolesSurface({ grantable = GRANTABLE_ACTIONS }: { grantable?: GrantableAction[] }) {
  const t = useT()
  const { can, tenantName: tenant } = useAccess()
  const canAssignRoles = can('roles.assign')
  const canDefine = can('roles.define')
  const rootRef = React.useRef<HTMLDivElement>(null)
  const roles = useRoles(true)
  const list = React.useMemo(() => roles.roles ?? [], [roles.roles])

  const [result, setResult] = React.useState<Result | null>(null)
  const [editor, setEditor] = React.useState<RoleEditorTarget | null>(null)
  const [members, setMembers] = React.useState<RoleRow | null>(null)
  const [deleting, setDeleting] = React.useState<RoleRow | null>(null)

  if (roles.error instanceof PeopleRequestError && roles.error.status === 403) return <RefusedSurface />

  let state: TableState
  if (roles.error) state = 'error'
  else if (!roles.roles) state = 'loading'
  else state = 'data'

  const byId = new Map(list.map((role) => [role.id, role]))
  const rows: TableRow[] = list.map((role) => ({
    id: role.id,
    label: roleLabel(t, role),
    cells: {
      role: {
        primary: roleLabel(t, role),
        secondary: role.kind === 'builtIn' ? builtInDescription(t, role.id, tenant) : undefined,
        pills: [
          role.kind === 'builtIn'
            ? { tone: 'neutral' as const, label: t('people.rolesPage.builtInPill') }
            : { tone: 'info' as const, label: t('people.rolesPage.customPill') },
        ],
      },
      holders: { primary: t('people.rolesPage.holders', { count: role.holderCount }) },
    },
  }))

  const renderActions = (row: TableRow) => {
    const role = byId.get(row.id)
    if (!role) return null
    const label = roleLabel(t, role)
    if (!role.canAssign) {
      return (
        <span className="text-xs text-fg-muted">
          {role.kind === 'builtIn' ? t('people.rolesPage.lockedForYou') : t('people.rolesPage.beyondYou')}
        </span>
      )
    }
    const editable = role.kind === 'custom' && role.canChange && canDefine
    return (
      <>
        {canAssignRoles ? (
          <Button
            variant="secondary"
            size="sm"
            aria-label={t('people.rolesPage.membersName', { role: label })}
            onClick={() => setMembers(role)}
          >
            {t('people.rolesPage.members')}
          </Button>
        ) : null}
        {editable ? (
          <>
            <Button
              variant="secondary"
              size="sm"
              aria-label={t('people.rolesPage.editName', { role: label })}
              onClick={() => setEditor({ mode: 'edit', role })}
            >
              {t('people.rolesPage.edit')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              aria-label={t('people.rolesPage.deleteName', { role: label })}
              onClick={() => setDeleting(role)}
            >
              {t('people.rolesPage.delete')}
            </Button>
          </>
        ) : null}
      </>
    )
  }

  const noCustomRoles = state === 'data' && !list.some((role) => role.kind === 'custom')

  return (
    <PeopleFrame
      rootRef={rootRef}
      pageTitle={t('people.rolesPage.title')}
      summary={t('people.rolesPage.summary', { tenant })}
      action={
        canDefine ? (
          <Button variant="primary" onClick={() => setEditor({ mode: 'create' })}>
            <Plus />
            {t('people.rolesPage.create')}
          </Button>
        ) : undefined
      }
    >
      <ResultMessage result={result} />

      <ServerDataTable
        layout="stacked"
        state={state}
        onRetry={() => void roles.revalidate()}
        columns={[
          { key: 'role', label: t('people.rolesPage.column') },
          { key: 'holders', label: t('people.rolesPage.holdersColumn'), width: '8rem' },
        ]}
        rows={rows}
        rowActions={{ label: t('people.users.actions'), render: renderActions }}
        caption={t('people.rolesPage.caption', { tenant })}
        regionLabel={t('people.rolesPage.caption', { tenant })}
        footer={state === 'loading' ? t('people.rolesPage.loading') : t('people.rolesPage.count', { count: list.length })}
        loadingRowCount={3}
        messages={{
          loadErrorTitle: t('people.rolesPage.loadFailed.title'),
          loadErrorBody: t('people.rolesPage.loadFailed.body'),
          retry: t('people.users.loadFailed.retry'),
        }}
      />
      {noCustomRoles ? <p className="text-sm text-fg-muted">{t('people.rolesPage.customEmpty')}</p> : null}

      <RoleEditorPanel
        target={editor}
        grantable={grantable}
        existingNames={new Set(list.map((role) => roleLabel(t, role).toLowerCase()))}
        onClose={() => setEditor(null)}
        onSaved={(role, created) => {
          setResult({ kind: created ? 'created' : 'saved', role: role.name })
          setEditor(null)
        }}
      />

      <MembersPanel
        role={members}
        onClose={() => setMembers(null)}
        onSaved={(role) => {
          setResult({ kind: 'membersSaved', role: roleLabel(t, role) })
          setMembers(null)
        }}
      />

      <DeleteRoleModal
        role={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={(role) => {
          setResult({ kind: 'deleted', role: roleLabel(t, role) })
          setDeleting(null)
          focusWithin(rootRef.current, 'h1')
        }}
        onOpenMembers={(role) => {
          setDeleting(null)
          setMembers(role)
        }}
      />
    </PeopleFrame>
  )
}

function ResultMessage({ result }: { result: Result | null }) {
  const t = useT()
  if (!result) return null
  switch (result.kind) {
    case 'created':
      return <OperationFeedback state="success" title={t('people.editor.created', { role: result.role })} />
    case 'saved':
      return <OperationFeedback state="success" title={t('people.editor.saved', { role: result.role })} />
    case 'membersSaved':
      return <OperationFeedback state="success" title={t('people.members.saved', { role: result.role })} />
    case 'deleted':
      return <OperationFeedback state="success" title={t('people.delete.done', { role: result.role })} />
  }
}

/*
 * Delete a custom role (VIS-332). If deleting it would leave someone with no
 * role, Identity refuses and names them; the dialog lists them and offers the
 * role's members instead.
 */
function DeleteRoleModal({
  role,
  onClose,
  onDeleted,
  onOpenMembers,
}: {
  role: RoleRow | null
  onClose: () => void
  onDeleted: (role: RoleRow) => void
  onOpenMembers: (role: RoleRow) => void
}) {
  const t = useT()
  const { can, tenantName: tenant } = useAccess()
  const mutations = usePeopleMutations()
  const [problem, setProblem] = React.useState<'failed' | 'refused' | 'changedMeanwhile' | null>(null)
  const [holders, setHolders] = React.useState<Holder[] | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [shownFor, setShownFor] = React.useState(role)
  if (role !== shownFor) {
    setShownFor(role)
    setProblem(null)
    setHolders(null)
  }

  const label = role ? roleLabel(t, role) : ''
  const blocked = holders !== null

  const confirm = async () => {
    if (!role || busy) return
    setBusy(true)
    setProblem(null)
    try {
      await mutations.deleteRole(role.id)
      onDeleted(role)
    } catch (error) {
      const code = errorCode(error)
      if (code === 'holder_without_role' && error instanceof PeopleRequestError) {
        setHolders(error.holders)
        void mutations.revalidate()
      } else if (errorStatus(error) === 403) {
        setProblem('refused')
      } else if (errorStatus(error) === 404) {
        setProblem('changedMeanwhile')
        void mutations.revalidate()
      } else {
        setProblem('failed')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={role !== null} onClose={onClose} labelledBy="delete-role-title" describedBy="delete-role-body">
      {role ? (
        <div className="flex flex-col gap-4">
          <div>
            <h2 id="delete-role-title" className="text-lg font-semibold text-fg-strong wrap-anywhere">
              {blocked ? t('people.delete.blockedTitle', { role: label }) : t('people.delete.title', { role: label })}
            </h2>
            <p id="delete-role-body" className="mt-1 text-sm text-fg-muted">
              {blocked
                ? t('people.delete.blockedBody', { role: label, tenant })
                : t('people.delete.body', { count: role.holderCount })}
            </p>
          </div>
          {blocked ? (
            <div>
              <p className="text-xs font-medium text-fg-muted">{t('people.delete.blockedList', { role: label })}</p>
              <ul className="mt-1 flex flex-col gap-0.5 text-sm text-fg">
                {holders.map((holder) => (
                  <li key={holder.userId} className="wrap-anywhere">
                    {holder.name ?? holder.email ?? holder.userId}
                    {holder.name && holder.email ? <span className="text-fg-muted"> ({holder.email})</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {problem === 'failed' ? (
            <OperationFeedback state="error" title={t('people.delete.failedTitle')} detail={t('people.delete.failedBody')} />
          ) : null}
          {problem === 'changedMeanwhile' ? (
            <OperationFeedback state="error" title={t('people.editor.changedMeanwhile')} />
          ) : null}
          {problem === 'refused' ? (
            <OperationFeedback
              state="error"
              title={t('core.access.actionRefusedTitle')}
              detail={t('core.access.actionRefusedBody', { tenant })}
            />
          ) : null}
          <p role="status" className="sr-only">
            {busy ? t('people.delete.working') : ''}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" autoFocus onClick={onClose}>
              {blocked || problem === 'changedMeanwhile' ? t('people.delete.close') : t('people.delete.cancel')}
            </Button>
            {blocked ? (
              can('roles.assign') ? (
                <Button variant="primary" onClick={() => onOpenMembers(role)}>
                  {t('people.delete.openMembers')}
                </Button>
              ) : null
            ) : problem === 'changedMeanwhile' ? null : (
              <Button variant="destructive" aria-disabled={busy || undefined} onClick={() => void confirm()}>
                {t('people.delete.confirm')}
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  )
}
